import React from 'react';
import { Box } from '@mui/material';

import { AngleField, DistanceField } from '@paragon/material';

import { updateShapeDetailsAndRegenerateTruss } from '../../../../../../store/actions/insertComponentDesignActions';
import { ScissorsShapeDetails as ScissorsShapeDetailsInterface } from '../../../../../../store/slices/applicationSlice';
import { useAppDispatch } from '../../../../../hooks';
import { LabeledBox } from '../../../properties/LabeledBox';
import { OverhangSettings } from '../../../properties/OverhangSettings';
import { createExceedsMaxLengthErrorAction, exceedsMaxLength, minTrussLength } from './Length';
import { ShapeDetailsHeelSettings } from './ShapeDetailsHeelSettings';

const textFieldSx = { m: 1, flex: 1 } as const;

interface Props {
  readonly shapeDetails: ScissorsShapeDetailsInterface;
}

export function ScissorsShapeDetails({ shapeDetails }: Props) {
  const dispatch = useAppDispatch();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          label="Length"
          value={shapeDetails.span}
          min={minTrussLength}
          onSubmit={async (value) => {
            if (exceedsMaxLength(value)) {
              dispatch(createExceedsMaxLengthErrorAction());
              return;
            }

            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, span: value }));
          }}
          sx={textFieldSx}
        />
        <AngleField
          label="Top Chord Slope"
          sx={textFieldSx}
          value={shapeDetails.topChordSlopeInDegrees}
          defaultDisplayFormat="slope"
          onSubmit={async (value) => {
            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, topChordSlopeInDegrees: value }));
          }}
        />
        <AngleField
          label="Bottom Chord Slope"
          sx={textFieldSx}
          value={shapeDetails.bottomChordSlopeInDegrees}
          defaultDisplayFormat="slope"
          onSubmit={async (value) => {
            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, bottomChordSlopeInDegrees: value }));
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <LabeledBox title="Heel">
          <ShapeDetailsHeelSettings
            heelSettings={shapeDetails.heelSettings}
            topChordSlopeInDegrees={shapeDetails.topChordSlopeInDegrees}
            onChange={async (value) => {
              await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, heelSettings: value }));
            }}
          />
        </LabeledBox>

        <LabeledBox title="Overhang">
          <OverhangSettings
            overhangSettings={shapeDetails.overhang}
            onChange={async (value) => {
              await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, overhang: value }));
            }}
          />
        </LabeledBox>
      </Box>
    </>
  );
}
